import { Row, Col, Card, Statistic, Progress, Tooltip } from 'antd';
import {
  LineChartOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
  AimOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { t } from '../../i18n/i18n';
import { oneDecimal } from '../../utils/numbersUtils';
import { convertSecondsToTimeFormat } from '../../utils/time';
import './TestGroupMetricsDetail.scss';

const TestGroupMetricsDetail = ({ metrics }) => {
  if (!metrics) return null;

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    return convertSecondsToTimeFormat(seconds);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    return `${oneDecimal(value)}%`;
  };

  const formatRatio = (value) => {
    if (value === null || value === undefined) return '-';
    return oneDecimal(value);
  };

  return (
    <div className="testgroup-metrics-detail">
      <Card title={t('TESTGROUP_ADVANCED_METRICS')} className="metrics-card">
        <Row gutter={[16, 16]}>
          {/* Standard Deviation */}
          <Col xs={24} sm={12} md={6}>
            <Card className="metric-item">
              <Statistic
                title={
                  <span>
                    {t('METRIC_STANDARD_DEVIATION')}
                    <Tooltip title={t('METRIC_STANDARD_DEVIATION_TOOLTIP')}>
                      <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                  </span>
                }
                value={oneDecimal(metrics.standardDeviation) || '-'}
                prefix={<LineChartOutlined />}
              />
            </Card>
          </Col>

          {/* Average Lead Time */}
          <Col xs={24} sm={12} md={6}>
            <Card className="metric-item">
              <Statistic
                title={
                  <span>
                    {t('METRIC_AVG_LEAD_TIME')}
                    <Tooltip title={t('METRIC_AVG_LEAD_TIME_TOOLTIP')}>
                      <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                  </span>
                }
                value={formatTime(metrics.averageLeadTime)}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>

          {/* Completion Rate */}
          <Col xs={24} sm={12} md={6}>
            <Card className="metric-item">
              <Statistic
                title={
                  <span>
                    {t('METRIC_COMPLETION_RATE')}
                    <Tooltip title={t('METRIC_COMPLETION_RATE_TOOLTIP')}>
                      <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                  </span>
                }
                value={formatPercentage(metrics.completionRate)}
                prefix={<PercentageOutlined />}
              />
              {metrics.completionRate !== null && metrics.completionRate !== undefined && (
                <Progress
                  percent={metrics.completionRate}
                  size="small"
                  status={metrics.completionRate >= 75 ? 'success' : 'normal'}
                />
              )}
            </Card>
          </Col>

          {/* Average Ratio (AI Questions) */}
          <Col xs={24} sm={12} md={6}>
            <Card className="metric-item">
              <Statistic
                title={
                  <span>
                    {t('METRIC_AVG_RATIO_AI')}
                    <Tooltip title={t('METRIC_AVG_RATIO_AI_TOOLTIP')}>
                      <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                  </span>
                }
                value={formatRatio(metrics.averageRatio)}
                prefix={<AimOutlined />}
                suffix="/1.0"
              />
              {metrics.averageRatio !== null && metrics.averageRatio !== undefined && (
                <Progress
                  percent={metrics.averageRatio * 100}
                  size="small"
                  format={() => `${formatRatio(metrics.averageRatio)}`}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Role-based metrics if available */}
        {metrics.dataGrouped && metrics.dataGrouped.length > 0 && (
          <div className="role-metrics-section">
            <h4>{t('METRIC_BY_ROLE')}</h4>
            <Row gutter={[16, 16]}>
              {metrics.dataGrouped.map((companyData) => (
                <Col key={companyData.companyId} span={24}>
                  <Card
                    size="small"
                    title={
                      companyData.companyId === 0
                        ? t('GENERAL_METRICS')
                        : `${t('COMPANY')} ${companyData.companyId}`
                    }
                  >
                    <Row gutter={[8, 8]}>
                      {Object.entries(companyData.roleMetrics || {}).map(([role, roleMetric]) => (
                        <Col key={role} xs={24} sm={12} md={8}>
                          <div className="role-metric-item">
                            <h5>{role === 'general' ? t('ALL_ROLES') : role}</h5>
                            <div className="role-metric-stats">
                              {roleMetric.standardDeviation !== undefined && (
                                <div className="stat-item">
                                  <span className="stat-label">{t('STD_DEV')}:</span>
                                  <span className="stat-value">
                                    {oneDecimal(roleMetric.standardDeviation)}
                                  </span>
                                </div>
                              )}
                              {roleMetric.completionRate !== undefined && (
                                <div className="stat-item">
                                  <span className="stat-label">{t('COMPLETION')}:</span>
                                  <span className="stat-value">
                                    {formatPercentage(roleMetric.completionRate)}
                                  </span>
                                </div>
                              )}
                              {roleMetric.averageRatio !== undefined && (
                                <div className="stat-item">
                                  <span className="stat-label">{t('AVG_RATIO')}:</span>
                                  <span className="stat-value">
                                    {formatRatio(roleMetric.averageRatio)}
                                  </span>
                                </div>
                              )}
                              {roleMetric.averageLeadTime !== undefined && (
                                <div className="stat-item">
                                  <span className="stat-label">{t('AVG_TIME')}:</span>
                                  <span className="stat-value">
                                    {formatTime(roleMetric.averageLeadTime)}
                                  </span>
                                </div>
                              )}
                              <div className="stat-item">
                                <span className="stat-label">{t('CANDIDATES')}:</span>
                                <span className="stat-value">{roleMetric.totalCount}</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TestGroupMetricsDetail;
